import type { TrainingWeekRecord } from '~/models/trainingWeek'
import { type CalendarDate, unwrapDate } from '~/types/dates'
import type { ProjectId, TrainingWeekId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'
import { withTransaction } from '../transactions'

/**
 * Materialised weeks (D-105 §6).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE WEEK IS FOUND BY TWO INDEXES, NEVER BY ITS START DATE AS A KEY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The store is keyed by an opaque id, following `weeklyReviews`. A mid-project
 *  change of `weekStartDay` and a project started mid-week each produce two
 *  records claiming the same day, so a natural key would collide exactly where
 *  the data matters most. **Every lookup here therefore filters the
 *  `weekStartDate` index by `projectId`** — a week belongs to one project, and
 *  the pair is what identifies it.
 */

export async function getTrainingWeek(
  db: SolaDatabase,
  id: TrainingWeekId,
): Promise<Result<TrainingWeekRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.trainingWeeks, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** A project's weeks, oldest first — the `projectId` index gives insertion order. */
export async function listTrainingWeeks(
  db: SolaDatabase,
  projectId: ProjectId,
): Promise<Result<readonly TrainingWeekRecord[], StorageError>> {
  try {
    const weeks = await db.getAllFromIndex(STORE.trainingWeeks, 'projectId', projectId)
    return ok(
      [...weeks].sort((left, right) =>
        unwrapDate(left.weekStartDate).localeCompare(unwrapDate(right.weekStartDate)),
      ),
    )
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function findTrainingWeek(
  db: SolaDatabase,
  projectId: ProjectId,
  weekStartDate: CalendarDate,
): Promise<Result<TrainingWeekRecord | undefined, StorageError>> {
  try {
    const sameWeek = await db.getAllFromIndex(
      STORE.trainingWeeks,
      'weekStartDate',
      unwrapDate(weekStartDate),
    )
    return ok(sameWeek.find((week) => week.projectId === projectId))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function putTrainingWeek(
  db: SolaDatabase,
  week: TrainingWeekRecord,
): Promise<Result<TrainingWeekRecord, StorageError>> {
  try {
    await db.put(STORE.trainingWeeks, week)
    notifyWritten(STORE.trainingWeeks)
    return ok(week)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export interface MaterialisationOutcome {
  readonly week: TrainingWeekRecord
  /** False when a record for this week already existed and was returned instead. */
  readonly created: boolean
}

/**
 * Write a materialised week, unless one already exists for that project and week.
 *
 * **Idempotent by construction rather than by remembering to check first.** The
 * lookup and the write are one read-write transaction, so two reads racing on
 * first open cannot both conclude the week is absent and write two records —
 * which would leave the app reading whichever the index returned and losing every
 * outcome recorded against the other.
 *
 * Nothing inside the transaction awaits anything but IndexedDB. Awaiting any
 * other promise would commit it early and reintroduce exactly that race (see
 * `transactions.ts`).
 */
export async function materialiseTrainingWeek(
  db: SolaDatabase,
  week: TrainingWeekRecord,
): Promise<Result<MaterialisationOutcome, StorageError>> {
  const written = await withTransaction(db, [STORE.trainingWeeks] as const, async (tx) => {
    const store = tx.objectStore(STORE.trainingWeeks)
    const sameWeek = await store.index('weekStartDate').getAll(unwrapDate(week.weekStartDate))
    const existing = sameWeek.find((candidate) => candidate.projectId === week.projectId)
    if (existing !== undefined) return { week: existing, created: false }

    await store.put(week)
    return { week, created: true }
  })
  if (!written.ok) return err(written.error)

  if (written.value.created) notifyWritten(STORE.trainingWeeks)
  return ok(written.value)
}

export { storageError }
